import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const Comox = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.comox:About Comox Paragraph 1">
          Nestled on Vancouver Island within the realm of{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Comox captivates with its coastal allure and vibrant cultural scene.
          Embraced by the Comox Glacier and Salish Sea, the town offers a haven
          for outdoor enthusiasts, encompassing activities from hiking to marine
          life exploration. Comox Marina and its arts and culture hub showcase
          the town’s identity, while its charming downtown exudes warmth. This
          close-knit community thrives through local events and festivals,
          fostering camaraderie. While the cost of living and ferry access
          present considerations, Comox stands as a harmonious intersection of
          nature and community, embodying the essence of captivating{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,400 - $2,400',
          bed2: '$1,600 - $2,800',
          bed3: '$2,300 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.comox:Apartment Hunting in Comox Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>cheap rentals in Comox</LinkText> –
            although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Apartments for rent Comox Paragraph">
              Looking for{' '}
              <LinkText href={links.apartment}>
                apartments for rent in Comox
              </LinkText>{' '}
              typically means enjoying a modern, low-maintenance lifestyle. Many
              of these apartments are located in newer buildings, with
              privileges, such as balconies offering scenic views, enhancing
              your living experience. These apartments for rent cater well to
              singles, couples, or small families who appreciate the convenience
              of being close to town centers, shops, and services. Finding such
              apartments for rent in Comox could be a real catch for those
              seeking a convenient and comfortable lifestyle.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Houses for rent Comox Paragraph">
              Those seeking{' '}
              <LinkText href={links.house}>houses for rent in Comox</LinkText>{' '}
              often enjoy more generous spaces, including yards - something
              that’s particularly ideal for families or individuals seeking
              privacy and a connection with the iconic outdoor lifestyle that
              the area echoes. Houses for rent in Comox come in various forms,
              from older, character-filled homes to their newer, more modern
              counterparts. Houses in Comox frequently radiate the rustic charm
              intrinsic to the region.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Townhouses for rent Comox Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Comox
              </LinkText>{' '}
              provide a balance between the privacy of a standalone house and
              the convenience of an apartment. These rented townhouses are often
              part of professionally managed communities, sometimes offering
              shared amenities like playgrounds or pools. Such townhouses for
              rent are a popular choice among small families or professionals
              seeking a community feel with just that bit more space. Therefore,
              the appeal of townhouses for rent lies in their blend of private
              and shared living.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Basements for rent Comox Paragraph">
              Basements for rent in Comox provide an affordable option, making
              them viable choices for many renters. These units, often part of a
              larger house, offer a cozy and occasionally more secluded living
              environment. They are a popular choice for students, singles, or
              those looking for basements for rent that present budget-friendly
              options.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Condos for rent Comox Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Comox
              </LinkText>{' '}
              blend the ease of apartment living with a sense of ownership and
              community. Renters can enjoy shared facilities such as fitness
              centers or communal gardens, and these units often come with
              modern fittings and appliances, appealing to those seeking a
              contemporary lifestyle. With condos for rent growing in
              popularity, Comox continues to offer a home-like environment to
              its residents.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Rooms for rent Comox Paragraph">
              <LinkText href={links.room}>Renting a room in Comox</LinkText> is
              an economical and flexible option, often preferred by students or
              young professionals. It involves sharing common areas like the
              kitchen and living room with other occupants, offering a communal
              living experience that can be both social and cost-effective.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Short term rentals Comox Paragraph">
              Comox, while offering a range of work opportunities and access to
              good academic institutions, may not necessarily be a primary
              destination for short-term renters. The popularity of short-term
              rentals often varies from city to city, and while they can be more
              sought-after during the summer months and at the start of school
              semesters in some areas, this trend is not universal. The
              flexibility of{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText> is
              an attractive feature for many renters, and it plays a significant
              role in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                determining if short-term rentals are a suitable option
              </LinkText>{' '}
              for them.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.comox:Cheap rentals Comox Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>cheap rentals in Comox</LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Comox, there are neighbourhoods where
              rent is considerably cheaper, so exploring these options is a good
              idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.comox:Living in Comox Paragraph">
            Living in Comox, British Columbia, offers a unique blend of
            small-town charm and stunning natural beauty. Nestled in the heart
            of the Comox Valley, this coastal town is surrounded by lush
            forests, pristine beaches, and the majestic Comox Glacier. The
            community is warm and welcoming, with an array of amenities that
            cater to both its residents and visitors. From delicious local
            cuisine to a vibrant arts scene, and a range of recreational
            activities, there’s something for everyone in Comox. The quality of
            life is exceptional, marked by clean air, safe neighborhoods, and a
            relaxed pace of life that truly sets it apart. The picturesque views
            and tranquility of Comox are ideal for those seeking a serene and
            peaceful living environment.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.comox:Living in Comox Paragraph 2">
            If you’re not sure that Comox is the right city for you, check out
            some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.comox:Popular Neighbourhoods Comox Paragraph">
          Like any city, Comox is made up of smaller neighbourhoods with their
          own distinct features. Some of the most popular neighbourhoods include
          Downtown Comox, Comox Peninsula, Cape Lazo, Beckton Estates, Point
          Holmes, Kye Bay, Comox Valley, and Foxxwood Heights. For more info
          about Comox, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.comox:Local Landmarks Comox Paragraph">
          Comox, British Columbia, is known for its breathtaking natural
          landmarks and historic sites. The Comox Air Force Museum, home to a
          wealth of aviation history, is a must-visit for history enthusiasts.
          The Filberg Heritage Lodge and Park, a beautiful nine-acre estate,
          offers stunning views and hosts the annual Filberg Festival. The Goose
          Spit Park, a unique coastal sandspit, provides panoramic vistas of the
          ocean and mountains. Another significant landmark is the Comox Marina,
          where you can admire the vast array of boats and enjoy spectacular
          sunsets. Furthermore, the Seal Bay Nature Park is a local favorite for
          peaceful walks amidst lush forest and sea views. These landmarks
          contribute to the unique charm and character of Comox.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Comox
